import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Select,
  MenuItem,
  Typography,
  Button,
  Slider,
  Skeleton,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { markdownComponents } from '../components/markdownComponents';
import { streamChat, AVAILABLE_MODELS, getChatHistory, saveChatMessages, clearChatHistory, type ChatMessage } from '../services/ai';
import { getItem } from '../services/items';
import { api } from '../lib/api';
import type { Item } from '../types';
import ItemCard from '../components/items/ItemCard';
import DocumentCard, { type DocumentInfo } from '../components/items/DocumentCard';

const FONT_OPTIONS = [
  { id: 'mono', label: 'Mono', family: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace" },
  { id: 'sans', label: 'Sans', family: "'Inter', 'Roboto', sans-serif" },
  { id: 'serif', label: 'Serif', family: "'Georgia', 'Times New Roman', serif" },
  { id: 'system', label: 'System', family: 'system-ui, sans-serif' },
];

const FONT_SIZE_MIN = 12;
const FONT_SIZE_MAX = 22;
const FONT_SIZE_DEFAULT = 14;

function loadPref<T>(key: string, fallback: T): T {
  try {
    const v = localStorage.getItem(`ai_${key}`);
    return v !== null ? JSON.parse(v) : fallback;
  } catch {
    return fallback;
  }
}

function savePref(key: string, value: unknown) {
  localStorage.setItem(`ai_${key}`, JSON.stringify(value));
}

// --- Item reference rendering ---

function MarkdownText({ text, fontFamily, fontSize }: { text: string; fontFamily: string; fontSize: number }) {
  return (
    <Box
      sx={{
        fontFamily,
        fontSize,
        '& p': { m: 0, mb: 0.75 },
        '& p:last-child': { mb: 0 },
        '& h1,& h2,& h3,& h4': { fontFamily, mt: 1, mb: 0.5 },
        '& ul,& ol': { pl: 2.5, my: 0.5 },
        '& li': { mb: 0.25 },
        '& code': { fontFamily: 'monospace', fontSize: '0.9em', bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5 },
        '& pre': { bgcolor: 'action.hover', p: 1, borderRadius: 1, overflow: 'auto', my: 0.5 },
        '& pre code': { bgcolor: 'transparent', px: 0 },
        '& blockquote': { borderLeft: 3, borderColor: 'divider', pl: 1.5, ml: 0, color: 'text.secondary' },
        '& a': { color: 'primary.main' },
        '& strong': { fontWeight: 600 },
        '& table': { borderCollapse: 'collapse', width: '100%', my: 0.5 },
        '& th,& td': { border: 1, borderColor: 'divider', px: 1, py: 0.5, textAlign: 'left' },
        '& th': { bgcolor: 'action.hover', fontWeight: 600 },
      }}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{text}</ReactMarkdown>
    </Box>
  );
}

function MessageContent({
  text,
  fontFamily,
  fontSize,
  itemCache,
  docCache,
  onFetchItem,
  onFetchDoc,
  onItemClick,
}: {
  text: string;
  fontFamily: string;
  fontSize: number;
  itemCache: Record<string, Item>;
  docCache: Record<string, DocumentInfo>;
  onFetchItem: (id: string) => void;
  onFetchDoc: (id: string) => void;
  onItemClick: (item: Item) => void;
}) {
  // Extract item and doc IDs and trigger fetches
  const itemIds = useMemo(() => {
    const ids: string[] = [];
    let match;
    const regex = /\{\{item:([^}]+)\}\}/g;
    while ((match = regex.exec(text)) !== null) ids.push(match[1]);
    return [...new Set(ids)];
  }, [text]);

  const docIds = useMemo(() => {
    const ids: string[] = [];
    let match;
    const regex = /\{\{doc:([^}]+)\}\}/g;
    while ((match = regex.exec(text)) !== null) ids.push(match[1]);
    return [...new Set(ids)];
  }, [text]);

  useEffect(() => { itemIds.forEach((id) => onFetchItem(id)); }, [itemIds, onFetchItem]);
  useEffect(() => { docIds.forEach((id) => onFetchDoc(id)); }, [docIds, onFetchDoc]);

  // No refs — render as plain markdown
  if (itemIds.length === 0 && docIds.length === 0) {
    return <MarkdownText text={text} fontFamily={fontFamily} fontSize={fontSize} />;
  }

  // Split text on item/doc references and render cards inline
  const parts = text.split(/(\{\{(?:item|doc):[^}]+\}\})/g);

  return (
    <Box>
      {parts.map((part, i) => {
        const itemMatch = part.match(/^\{\{item:([^}]+)\}\}$/);
        if (itemMatch) {
          const item = itemCache[itemMatch[1]];
          if (item) {
            return (
              <Box key={i} sx={{ my: 1, maxWidth: 345 }}>
                <ItemCard item={item} onClick={onItemClick} />
              </Box>
            );
          }
          return (
            <Box key={i} sx={{ my: 1, maxWidth: 345 }}>
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
            </Box>
          );
        }

        const docMatch = part.match(/^\{\{doc:([^}]+)\}\}$/);
        if (docMatch) {
          const doc = docCache[docMatch[1]];
          if (doc) {
            return (
              <Box key={i} sx={{ my: 1 }}>
                <DocumentCard doc={doc} />
              </Box>
            );
          }
          return (
            <Box key={i} sx={{ my: 1, maxWidth: 380 }}>
              <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
            </Box>
          );
        }

        if (part) {
          return <MarkdownText key={i} text={part} fontFamily={fontFamily} fontSize={fontSize} />;
        }
        return null;
      })}
    </Box>
  );
}

function formatTime(iso?: string) {
  if (!iso) return null;
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

// --- Main page ---

export default function AiPage() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [model, setModel] = useState(AVAILABLE_MODELS[0].id);
  const [fontId, setFontId] = useState(() => loadPref('font', 'mono'));
  const [fontSize, setFontSize] = useState(() => loadPref('fontSize', FONT_SIZE_DEFAULT));
  const [itemCache, setItemCache] = useState<Record<string, Item>>({});
  const [docCache, setDocCache] = useState<Record<string, DocumentInfo>>({});
  const fetchingRef = useRef<Set<string>>(new Set());
  const fetchingDocsRef = useRef<Set<string>>(new Set());
  const pollingRef = useRef<Map<string, ReturnType<typeof setInterval>>>(new Map());
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const historyIndexRef = useRef(-1);

  const pollItem = useCallback((itemId: string) => {
    if (pollingRef.current.has(itemId)) return;
    const interval = setInterval(async () => {
      const updated = await getItem(itemId);
      if (updated) {
        setItemCache((prev) => ({ ...prev, [itemId]: updated }));
        if (updated.enrichmentStatus !== 'pending') {
          clearInterval(pollingRef.current.get(itemId)!);
          pollingRef.current.delete(itemId);
        }
      }
    }, 2000);
    pollingRef.current.set(itemId, interval);
  }, []);

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      pollingRef.current.forEach((interval) => clearInterval(interval));
      pollingRef.current.clear();
    };
  }, []);

  const handleFetchItem = useCallback(async (itemId: string) => {
    if (fetchingRef.current.has(itemId)) return;
    fetchingRef.current.add(itemId);
    const item = await getItem(itemId);
    if (item) {
      setItemCache((prev) => ({ ...prev, [itemId]: item }));
      if (item.enrichmentStatus === 'pending') {
        pollItem(itemId);
      }
    }
  }, [pollItem]);

  const handleFetchDoc = useCallback(async (docId: string) => {
    if (fetchingDocsRef.current.has(docId)) return;
    fetchingDocsRef.current.add(docId);
    try {
      const doc = await api<DocumentInfo>(`/api/documents/${docId}`);
      setDocCache((prev) => ({ ...prev, [docId]: doc }));
    } catch {
      // ignore
    }
  }, []);

  const handleItemClick = useCallback((item: Item) => {
    navigate(`/items/${item.id}`);
  }, [navigate]);

  // Load chat history from API on mount
  useEffect(() => {
    getChatHistory().then((history) => {
      setMessages(history);
      setHistoryLoading(false);
    }).catch(() => {
      setHistoryLoading(false);
    });
  }, []);

  // Auto-focus input once history has loaded
  useEffect(() => {
    if (!historyLoading) inputRef.current?.focus();
  }, [historyLoading]);

  const fontFamily = FONT_OPTIONS.find((f) => f.id === fontId)?.family ?? FONT_OPTIONS[0].family;

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    bottomRef.current?.scrollIntoView({ behavior });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingContent, statusMessage, scrollToBottom]);

  // On initial load, jump instantly to bottom once history is ready
  useEffect(() => {
    if (!historyLoading) scrollToBottom('instant');
  }, [historyLoading, scrollToBottom]);

  const handleSend = useCallback(async () => {
    const trimmed = input.trim();
    if (!trimmed || isStreaming) return;

    const userMessage: ChatMessage = { role: 'user', content: trimmed, createdAt: new Date().toISOString() };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    historyIndexRef.current = -1;
    setIsStreaming(true);
    setStreamingContent('');

    let accumulated = '';

    const MAX_HISTORY = 100;
    const recentHistory = messages.slice(-MAX_HISTORY);

    await streamChat(
      trimmed,
      recentHistory,
      model,
      (chunk) => {
        accumulated += chunk;
        setStreamingContent(accumulated);
        setStatusMessage('');
      },
      (status) => {
        setStatusMessage(status);
      },
      () => {
        const modelMessage: ChatMessage = { role: 'model', content: accumulated, createdAt: new Date().toISOString() };
        setMessages((prev) => [...prev, modelMessage]);
        setStreamingContent('');
        setStatusMessage('');
        setIsStreaming(false);
        saveChatMessages([userMessage, modelMessage]).catch(() => {});
        setTimeout(() => { if (!document.activeElement || document.activeElement === document.body) inputRef.current?.focus(); }, 0);
      },
      (error: string) => {
        const errorMessage: ChatMessage = { role: 'model', content: `Error: ${error}`, createdAt: new Date().toISOString() };
        setMessages((prev) => [...prev, errorMessage]);
        setStreamingContent('');
        setStatusMessage('');
        setIsStreaming(false);
        saveChatMessages([userMessage, errorMessage]).catch(() => {});
        setTimeout(() => { if (!document.activeElement || document.activeElement === document.body) inputRef.current?.focus(); }, 0);
      },
    );
  }, [input, isStreaming, messages, model]);

  const userHistory = useMemo(
    () => messages.filter((m) => m.role === 'user').map((m) => m.content),
    [messages],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
    if (e.key === 'ArrowUp' && !isStreaming && userHistory.length > 0) {
      // Only navigate history when cursor is at position 0 (or input is empty)
      const el = inputRef.current;
      if (el && el.selectionStart !== 0) return;
      e.preventDefault();
      const nextIndex = Math.min(historyIndexRef.current + 1, userHistory.length - 1);
      historyIndexRef.current = nextIndex;
      setInput(userHistory[userHistory.length - 1 - nextIndex]);
    }
    if (e.key === 'ArrowDown' && !isStreaming && historyIndexRef.current >= 0) {
      const el = inputRef.current;
      if (el && el.selectionStart !== el.value.length) return;
      e.preventDefault();
      const nextIndex = historyIndexRef.current - 1;
      historyIndexRef.current = nextIndex;
      if (nextIndex < 0) {
        setInput('');
      } else {
        setInput(userHistory[userHistory.length - 1 - nextIndex]);
      }
    }
  };

  const handleClear = () => {
    if (!window.confirm('Clear conversation history?')) return;
    setMessages([]);
    clearChatHistory().catch(() => {});
    setStreamingContent('');
    setStatusMessage('');
    setInput('');
    setItemCache({});
    setDocCache({});
    fetchingRef.current.clear();
    fetchingDocsRef.current.clear();
    pollingRef.current.forEach((interval) => clearInterval(interval));
    pollingRef.current.clear();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: { xs: 'calc(100dvh - 72px)', sm: 'calc(100dvh - 96px)', md: 'calc(100dvh - 112px)' }, minHeight: 0 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1, flexWrap: 'wrap', flexShrink: 0 }}>
        <Typography variant="h5">AI Chat</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Select
            size="small"
            value={fontId}
            onChange={(e) => { setFontId(e.target.value); savePref('font', e.target.value); }}
            sx={{ minWidth: 90, fontSize: '0.75rem' }}
          >
            {FONT_OPTIONS.map((f) => (
              <MenuItem key={f.id} value={f.id} sx={{ fontSize: '0.75rem' }}>
                {f.label}
              </MenuItem>
            ))}
          </Select>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 120 }}>
            <Typography variant="caption" color="text.secondary">{fontSize}px</Typography>
            <Slider
              size="small"
              min={FONT_SIZE_MIN}
              max={FONT_SIZE_MAX}
              value={fontSize}
              onChange={(_, v) => { setFontSize(v as number); savePref('fontSize', v); }}
              sx={{ width: 80 }}
            />
          </Box>
          {messages.length > 0 && (
            <Button
              size="small"
              startIcon={<DeleteOutlineIcon />}
              onClick={handleClear}
              color="inherit"
            >
              Clear
            </Button>
          )}
        </Box>
      </Box>

      {/* Messages */}
      <Box
        onClick={() => inputRef.current?.focus()}
        sx={{
          flex: 1,
          overflow: 'auto',
          fontFamily,
          fontSize: `${fontSize}px`,
          lineHeight: 1.4,
          px: 1,
          pb: 1,
        }}
      >
        {historyLoading && messages.length === 0 && (
          <Typography color="text.secondary" sx={{ fontFamily, mt: 4, textAlign: 'center' }}>
            Loading history...
          </Typography>
        )}

        {!historyLoading && messages.length === 0 && !isStreaming && (
          <Typography
            color="text.secondary"
            sx={{ fontFamily, mt: 4, textAlign: 'center' }}
          >
            Ask anything about your saved items...
          </Typography>
        )}

        {messages.map((msg, i) => (
          <Box key={i} sx={{ mb: 0.5 }}>
            {msg.role === 'user' ? (
              <Box>
                <Box sx={{ color: 'primary.main', whiteSpace: 'pre-wrap' }}>
                  {'> '}{msg.content}
                </Box>
                {msg.createdAt && (
                  <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7em', display: 'block', mt: 0.25, lineHeight: 1.2 }}>
                    {formatTime(msg.createdAt)}
                  </Typography>
                )}
              </Box>
            ) : (
              <Box sx={{ ml: '25%' }}>
                <MessageContent
                  text={msg.content}
                  fontFamily={fontFamily}
                  fontSize={fontSize}
                  itemCache={itemCache}
                  docCache={docCache}
                  onFetchItem={handleFetchItem}
                  onFetchDoc={handleFetchDoc}
                  onItemClick={handleItemClick}
                />
                {msg.createdAt && (
                  <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7em', display: 'block', mt: 0.25, lineHeight: 1.2 }}>
                    {formatTime(msg.createdAt)}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        ))}

        {isStreaming && statusMessage && !streamingContent && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontStyle: 'italic', mb: 1 }}
          >
            {statusMessage}
          </Typography>
        )}

        {isStreaming && (
          <Box sx={{ mb: 0.5, whiteSpace: 'pre-wrap', ml: '25%' }}>
            {streamingContent}
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                width: '0.6em',
                height: '1.1em',
                bgcolor: 'text.primary',
                verticalAlign: 'text-bottom',
                animation: 'blink 1s step-end infinite',
                '@keyframes blink': {
                  '50%': { opacity: 0 },
                },
              }}
            />
          </Box>
        )}

        <div ref={bottomRef} />
      </Box>

      {/* Input bar */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', pt: 1, borderTop: 1, borderColor: 'divider' }}>
        <Select
          size="small"
          value={model}
          onChange={(e) => setModel(e.target.value)}
          sx={{ minWidth: 140, fontFamily, fontSize: '0.75rem' }}
        >
          {AVAILABLE_MODELS.map((m) => (
            <MenuItem key={m.id} value={m.id} sx={{ fontFamily, fontSize: '0.75rem' }}>
              {m.label}
            </MenuItem>
          ))}
        </Select>

        <TextField
          inputRef={inputRef}
          fullWidth
          multiline
          maxRows={4}
          size="small"
          placeholder="Message..."
          value={input}
          onChange={(e) => { setInput(e.target.value); historyIndexRef.current = -1; }}
          onKeyDown={handleKeyDown}
          disabled={isStreaming}
          sx={{ '& textarea': { fontFamily, fontSize: `${fontSize}px` } }}
        />

        <IconButton onClick={handleSend} disabled={!input.trim() || isStreaming} color="primary">
          <SendIcon />
        </IconButton>
      </Box>
    </Box>
  );
}
